import { merge, uniqueId } from 'lodash-es'

console.debug(`react-native-link: starting`)
console.debug(`react-native-link: is the ReactNativeWebView on the window? (${ window.ReactNativeWebView ? 'yes' : 'no'})`)

window.isNative = !!window.ReactNativeWebView

/* If we are running in an iFrame, and the outer window is running demophone.html */
if (window.self !== window.top && window.top.location.href.includes('demophone.html')) {
    /* Fake the native app */
    window.isNative = true
    window.appIsFirstRun = true
    window.appDemoPhone = true
    window.appVersion = 999999 // This is a fake version number
    window.ReactNativeWebView = {
        postMessage: (message) => {}
    }
}

/* If we are running the native app set a cookie to indicate the version, first run, and biometric support */
/* This is used to see the requests in the server logs. Don't use it to detect requests by the app */
/* For detecting requests from the native app on the server read the 'regicare-app' header instead */
if (window.isNative) {
    try {
        document.cookie = `_rc_app=v${window.appVersion}:${window.appIsFirstRun ? 'f':''}${window.isBiometricSupported ? 'b' : ''};path=/`
    } catch(e) {
        console.debug('error setting cookie', e)
    }
}

/* Stop here if there is no ReactNativeWebView */
if (window.ReactNativeWebView) {

    /* A simple store */
    const store = {
        state: {
            messages: {
                // 'exampleRef': {
                //     ref: 'exampleRef',
                //     fn: 'exampleFn',
                //     args: ['exampleArg'],
                //     status: 'FOO',
                //     result: 'BAR',
                //     error: 'SNAFU',
                // },
            },
        },

        addMessage(message) {
            if (this.state.messages[message.ref]) throw new Exception('Message with this ref already exists')
            this.state.messages[message.ref] = message
            console.debug('added message', message.ref, this.state.messages)
        },

        removeMessage(message) {
            delete this.state.messages[message.ref]
            console.debug('removed message', message.ref, this.state.messages)
        },

        updateMessage(message, updateType) {
            const msg = this.state.messages[message.ref] || {}
            this.state.messages[message.ref] = merge(msg, message)

            console.debug('updateMessage', msg)

            switch(updateType) {
                case('status'):
                    msg.statusCallback(msg.status)
                    break
                case('result'):
                    msg.resolve(msg.result)
                    this.removeMessage(message)
                    break
                case('error'):
                    msg.reject(msg.error)
                    this.removeMessage(message)
                    break
                default:
            }
        },

        postResult(message) {
            this.updateMessage(message, 'result')
        },
        postError(message) {
            this.updateMessage(message, 'error')
        },
        postStatus(message) {
            this.updateStatus(message, 'status')
        },

        async sendMessage(fn, options) {
            console.debug('sendMessage')
            return new Promise((resolve, reject) => {
                const ref = options.ref || uniqueId()
                delete options.ref
                const statusCallback = options.statusCallback || function() {}
                delete options.statusCallback

                const payload = {
                    ref, 
                    fn,
                    args: options,
                }
                
                window.ReactNativeWebView.postMessage(JSON.stringify(payload))

                const message = {
                    ...payload,
                    statusCallback,
                    resolve,
                    reject,
                }
                store.addMessage(message)
            })
        }
    }


    /* Set up functions for the native app to call */
    window.nativeLink = {

        /* Functions for message handling */
        postStatus: (message) => {
            console.debug('nativeLink postStatus', message)
            store.postStatus(message)
        },
        postResult: (message) => {
            console.debug('nativeLink postResult', message)
            store.postResult(message)
        },
        postError: (message) => {
            console.debug('nativeLink postError', message)
            store.postError(message)
        },

        /* Event handler for native app to push events to the web app */
        postEvent: (eventName, data) => {
            console.debug('nativeLink postEvent', eventName, data)
            document.dispatchEvent(new CustomEvent(`native:${eventName}`, { detail: data }))
        },

        /* Other functions */
        removeLoadingState: () => {
            /* Negates the onbeforeunload event styling in app-mobile.js */
            document.body.style.transition = ""
            document.body.style.opacity = 1
        }
    }


    /* Set up function for the web app to call */
    window.nativeCalls = {
        webReady: async (options = {}) => {
            return store.sendMessage('webReady', options)
        },

        rememberPage: async (options = {}) => {
            return store.sendMessage('rememberPage', options)
        },

        whitelistDomains: async (options = {}) => {
            return store.sendMessage('whitelistDomains', options)
        },

        setKeyValuePair: async (options = {}) => {
            console.debug('setKeyValuePair', options)
            return store.sendMessage('setKeyValuePair', options)
        },

        getKeyValuePair: async (options = {}) => {
            console.debug('getKeyValuePair', options)
            return store.sendMessage('getKeyValuePair', options)
        },

        reset: async (options = {}) => {
            return store.sendMessage('reset', options)
        },

        test: async (options = {}) => {
            return store.sendMessage('test', options)
        },

        getDeviceToken: async (options = {}) => {
            return store.sendMessage('getDeviceToken', options)
        },

        scheduleNotification: async (options = {}) => {
            return store.sendMessage('scheduleNotification', options)
        },

        displayNotification: async (options = {}) => {
            return store.sendMessage('displayNotification', options)
        },

        cancelNotification: async (options = {}) => {
            return store.sendMessage('cancelNotification', options)
        }
    }

    // example using promise and status callback
    // window.nativeCalls.test({
    //     ref: 'test-ref',
    //     statusCallback: () => {
    //         console.debug('this is the status callback implemented in web')
    //     },
    //     testMessage: 'foo'
    // }).then((result) => {
    //     console.debug(result)
    // })

}

/**
 * App Settings Helper
 * 
 * Provides a unified API for reading/writing app settings that works both
 * in the native app (using native storage) and in browsers (using localStorage).
 * 
 * Usage:
 *   const mapApp = await window.appSettings.getMapApp()
 *   await window.appSettings.setMapApp('waze')
 */
window.appSettings = {
    STORAGE_KEYS: {
        MAP_APP: 'preferred_map_app',
    },

    MAP_APPS: {
        GOOGLE_MAPS: 'google_maps',
        WAZE: 'waze',
        APPLE_MAPS: 'apple_maps',
    },

    /**
     * Detect if the device is running iOS
     */
    isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent)
    },

    /**
     * Get the default map app based on the platform
     */
    getDefaultMapApp() {
        return this.isIOS() ? this.MAP_APPS.APPLE_MAPS : this.MAP_APPS.GOOGLE_MAPS
    },

    /**
     * Get a value from storage (native or localStorage)
     */
    async getValue(key) {
        if (window.isNative && window.nativeCalls?.getKeyValuePair) {
            try {
                return await window.nativeCalls.getKeyValuePair({ key })
            } catch (e) {
                console.debug('appSettings.getValue error:', e)
                return null
            }
        }
        return localStorage.getItem(key)
    },

    /**
     * Set a value in storage (native or localStorage)
     */
    async setValue(key, value) {
        if (window.isNative && window.nativeCalls?.setKeyValuePair) {
            try {
                await window.nativeCalls.setKeyValuePair({ key, value })
                return true
            } catch (e) {
                console.debug('appSettings.setValue error:', e)
                return false
            }
        }
        localStorage.setItem(key, value)
        return true
    },

    /**
     * Get the user's preferred map app
     */
    async getMapApp() {
        const value = await this.getValue(this.STORAGE_KEYS.MAP_APP)
        return value || this.getDefaultMapApp()
    },

    /**
     * Set the user's preferred map app
     */
    async setMapApp(mapApp) {
        return await this.setValue(this.STORAGE_KEYS.MAP_APP, mapApp)
    },

    /**
     * Build a navigation URL for the given coordinates using the user's preferred map app
     */
    async buildNavigationUrl(lat, lng, label = null) {
        const mapApp = await this.getMapApp()
        const encodedLabel = label ? encodeURIComponent(label) : null

        switch (mapApp) {
            case this.MAP_APPS.WAZE:
                return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
            case this.MAP_APPS.APPLE_MAPS:
                return encodedLabel
                    ? `https://maps.apple.com/?daddr=${lat},${lng}&q=${encodedLabel}`
                    : `https://maps.apple.com/?daddr=${lat},${lng}`
            case this.MAP_APPS.GOOGLE_MAPS:
            default:
                return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        }
    },

    /**
     * Build a navigation URL for the given address string using the user's preferred map app
     */
    async buildNavigationUrlFromAddress(address) {
        const mapApp = await this.getMapApp()
        const encodedAddress = encodeURIComponent(address)

        switch (mapApp) {
            case this.MAP_APPS.WAZE:
                return `https://waze.com/ul?q=${encodedAddress}&navigate=yes`
            case this.MAP_APPS.APPLE_MAPS:
                return `https://maps.apple.com/?q=${encodedAddress}`
            case this.MAP_APPS.GOOGLE_MAPS:
            default:
                return `https://maps.google.com/?q=${encodedAddress}`
        }
    }
}